import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicProtocol } from '../api';

function CitationText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\[[^\]]+\d{4}[^\]]*])/g);
  return (
    <>
      {parts.map((part, i) =>
        /^\[.+\d{4}.+]$/.test(part) ? (
          <span
            key={i}
            className="inline-block text-xs font-medium px-1.5 py-0.5 rounded mx-0.5 align-baseline"
            style={{ background: '#fce7f3', color: '#9d174d', boxShadow: 'rgba(157,23,77,0.15) 0px 0px 0px 1px' }}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function ProtocolSection({ section, index }) {
  const lines = (section.content || '').split('\n').filter(Boolean);
  const isMethodsSection = /method|step|procedure/i.test(section.title);

  return (
    <section className="mb-8">
      <h2
        className="text-lg font-semibold text-[#171717] mb-3 pb-2"
        style={{ borderBottom: '1px solid #ebebeb', letterSpacing: '-0.32px' }}
      >
        {index + 1}. {section.title}
      </h2>
      {isMethodsSection ? (
        <ol className="space-y-3">
          {lines.map((line, i) => {
            const stripped = line.replace(/^\d+[.)]\s*/, '');
            return (
              <li key={i} className="flex gap-3">
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center mt-0.5"
                  style={{ background: '#fce7f3', color: '#9d174d' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm text-[#4d4d4d] leading-relaxed">
                  <CitationText text={stripped} />
                </p>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="space-y-2">
          {lines.map((line, i) => (
            <p key={i} className="text-sm text-[#4d4d4d] leading-relaxed">
              <CitationText text={line} />
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

export default function PublicProtocol() {
  const { slug } = useParams();
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getPublicProtocol(slug)
      .then((res) => {
        setProtocol(res.data);
        document.title = `${res.data.title} — Protovera`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute(
            'content',
            `AI-generated research protocol: ${res.data.title}. Created with Protovera — grounded in literature with inline citations.`
          );
        }
        // Article schema for GEO — injected per-protocol page
        const existingSchema = document.getElementById('protocol-schema');
        if (existingSchema) existingSchema.remove();
        const schema = document.createElement('script');
        schema.id = 'protocol-schema';
        schema.type = 'application/ld+json';
        schema.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': res.data.title,
          'description': `AI-generated research protocol: ${res.data.title}. Grounded in published literature with inline citations.`,
          'datePublished': res.data.created_at || new Date().toISOString(),
          'author': { '@type': 'Organization', 'name': 'Protovera', 'url': window.location.origin },
          'publisher': { '@type': 'Organization', 'name': 'Protovera', 'url': window.location.origin },
          'url': window.location.href,
          'keywords': 'research protocol, life sciences, experimental protocol, AI-generated protocol',
          'articleSection': 'Research Protocol',
          'inLanguage': 'en',
        });
        document.head.appendChild(schema);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError('This protocol is not publicly available or has been removed.');
        } else {
          setError('Failed to load protocol.');
        }
        document.title = 'Protocol Not Found — Protovera';
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <p className="text-sm text-[#808080]">Loading protocol...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-sm text-[#4d4d4d] mb-4">{error}</p>
          <Link to="/" className="text-sm font-medium text-[#0a72ef] hover:underline">
            ← Back to Protovera
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = protocol.created_at
    ? new Date(protocol.created_at).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Top banner */}
      <div
        className="w-full py-2.5 px-4 text-center text-xs"
        style={{ background: '#171717', color: '#fff' }}
      >
        Generated by{' '}
        <Link to="/" className="font-semibold underline hover:no-underline">
          Protovera
        </Link>
        {' '}— AI research protocol generator grounded in your literature.{' '}
        <Link
          to="/register"
          className="font-semibold underline hover:no-underline"
        >
          Try it free →
        </Link>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <header className="mb-8">
          <h1
            className="text-2xl font-bold text-[#171717] mb-2"
            style={{ letterSpacing: '-0.5px', lineHeight: 1.2 }}
          >
            {protocol.title}
          </h1>
          <div className="flex items-center gap-3 text-xs text-[#808080]">
            {formattedDate && <span>{formattedDate}</span>}
            <span
              className="px-2 py-0.5 rounded font-medium"
              style={{ background: '#f0f0f0', color: '#4d4d4d' }}
            >
              Research Protocol
            </span>
          </div>
        </header>

        {/* Sections */}
        <article>
          {protocol.sections.map((section, i) => (
            <ProtocolSection key={section.title || i} section={section} index={i} />
          ))}
        </article>

        {/* CTA footer */}
        <div
          className="mt-10 rounded-xl p-6 text-center"
          style={{ background: '#fff', boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 8px' }}
        >
          <p className="text-base font-semibold text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
            Generate your own research protocol
          </p>
          <p className="text-sm text-[#808080] mb-4">
            Search PubMed, arXiv, and bioRxiv, then turn your papers into grounded protocols with inline citations. Free to start.
          </p>
          <Link
            to="/register"
            className="inline-block px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors"
            style={{ background: '#171717' }}
            onMouseEnter={(e) => (e.target.style.background = '#2e2e2e')}
            onMouseLeave={(e) => (e.target.style.background = '#171717')}
          >
            Create free account →
          </Link>
        </div>
      </main>
    </div>
  );
}
